import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  ADD_SUSPENSIVE_CONDITION,
  APPROVE_DEAL_REVERT,
  COMPLETE_DEAL,
  GET_SALES_DEAL,
  MARK_DEAL_AS_CONCLUDED,
  REJECT_DEAL_REVERT,
  REMOVE_SUSPENSIVE_CONDITION,
  REQUEST_DEAL_REVERT,
  REVERT_DEAL_DIRECTLY,
  SET_BOND_GRANTED,
  SET_COMPLIANCE_CERTIFICATE,
  SET_DEPOSIT_RECEIVED,
  SET_FICA_CHECKED,
  SET_HOA_APPLICABLE,
  SET_SENT_TO_SMART_TRACK,
  SET_TRANSFER_MILESTONE,
  UPDATE_SUSPENSIVE_CONDITION
} from '../../graphql/sales-deals';
import { GET_PROFESSIONAL_SERVICES } from '../../graphql/professional-services';
import { isMasterUser } from '../../shared/utils/permissions';
import { useAuth } from '../../shared/contexts/auth-context';
import Modal from '../../components/Modal';

const formatDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString();
};

const STATUS_BADGE = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONCLUDED: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800'
};

const REVERT_STATUS_BADGE = {
  PENDING_APPROVAL: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-gray-200 text-gray-700',
  DIRECT: 'bg-blue-100 text-blue-800'
};

const REVERT_STATUS_LABEL = {
  PENDING_APPROVAL: 'Pending master approval',
  APPROVED: 'Approved & reverted',
  REJECTED: 'Rejected',
  DIRECT: 'Direct revert by master'
};

// Conveyancing milestones the attorney feeds back after CONCLUDED.
// Order matters — this is the order they're displayed and roughly the
// order they happen.
const TRANSFER_MILESTONES = [
  { key: 'sarsClearance', label: 'SARS clearance received' },
  { key: 'municipalityClearance', label: 'Municipality clearance received' },
  { key: 'hoaClearance', label: 'HOA clearance received', conditional: 'hoaApplicable' },
  { key: 'sellerSignedTransferAttorney', label: 'Seller signed with transfer attorney' },
  { key: 'purchaserSignedBondAttorney', label: 'Purchaser signed with bond attorney' },
  { key: 'purchaserSignedTransferAttorney', label: 'Purchaser signed with transfer attorney' },
  { key: 'lodgement', label: 'Lodged at Deeds Office' },
  { key: 'registrationPreparation', label: 'Prepared for registration at Deeds Office' },
  { key: 'registered', label: 'Registered at Deeds Office (transfer day)' }
];

const toDateInput = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '' : dt.toISOString().slice(0, 10);
};

const COMPLIANCE_CERTS = [
  { key: 'electrical', label: 'Electrical' },
  { key: 'gas', label: 'Gas' },
  { key: 'beetle', label: 'Beetle' }
];

const DealWorkflowPanel = ({ deal, refetch }) => {
  const auth = useAuth();
  const isMaster = isMasterUser(auth.user);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [revertModalOpen, setRevertModalOpen] = useState(false);
  const [decisionModal, setDecisionModal] = useState(null); // { requestId, kind: 'approve' | 'reject' }
  const [newCondition, setNewCondition] = useState({ description: '', dueDate: '' });
  const [revertReason, setRevertReason] = useState('');
  const [decisionNote, setDecisionNote] = useState('');

  const refetchOpts = {
    refetchQueries: [{ query: GET_SALES_DEAL, variables: { id: deal.id } }],
    awaitRefetchQueries: true,
    onError: (e) => alert(e.message || 'Operation failed')
  };

  const [setDepositReceived, { loading: depLoading }] = useMutation(SET_DEPOSIT_RECEIVED, refetchOpts);
  const [setBondGranted, { loading: bondLoading }] = useMutation(SET_BOND_GRANTED, refetchOpts);
  const [setSentToSmartTrack, { loading: stLoading }] = useMutation(SET_SENT_TO_SMART_TRACK, refetchOpts);
  const [updateCondition, { loading: condLoading }] = useMutation(UPDATE_SUSPENSIVE_CONDITION, refetchOpts);
  const [removeCondition] = useMutation(REMOVE_SUSPENSIVE_CONDITION, refetchOpts);
  const [addCondition, { loading: addLoading }] = useMutation(ADD_SUSPENSIVE_CONDITION, {
    ...refetchOpts,
    onCompleted: () => {
      setAddModalOpen(false);
      setNewCondition({ description: '', dueDate: '' });
    }
  });
  const [markConcluded, { loading: concludeLoading }] = useMutation(MARK_DEAL_AS_CONCLUDED, {
    ...refetchOpts,
    onCompleted: () => refetch?.()
  });
  const [requestRevert, { loading: reqLoading }] = useMutation(REQUEST_DEAL_REVERT, {
    ...refetchOpts,
    onCompleted: () => {
      setRevertModalOpen(false);
      setRevertReason('');
    }
  });
  const [revertDirectly, { loading: directLoading }] = useMutation(REVERT_DEAL_DIRECTLY, {
    ...refetchOpts,
    onCompleted: () => {
      setRevertModalOpen(false);
      setRevertReason('');
      refetch?.();
    }
  });
  const [approveRevert, { loading: approveLoading }] = useMutation(APPROVE_DEAL_REVERT, {
    ...refetchOpts,
    onCompleted: () => {
      setDecisionModal(null);
      setDecisionNote('');
      refetch?.();
    }
  });
  const [rejectRevert, { loading: rejectLoading }] = useMutation(REJECT_DEAL_REVERT, {
    ...refetchOpts,
    onCompleted: () => {
      setDecisionModal(null);
      setDecisionNote('');
    }
  });
  const [setMilestone, { loading: milestoneLoading }] = useMutation(SET_TRANSFER_MILESTONE, refetchOpts);
  const [setHoaApplicable, { loading: hoaLoading }] = useMutation(SET_HOA_APPLICABLE, refetchOpts);
  const [setComplianceCertificate, { loading: ccLoading }] = useMutation(SET_COMPLIANCE_CERTIFICATE, refetchOpts);
  const [setFicaChecked, { loading: ficaLoading }] = useMutation(SET_FICA_CHECKED, refetchOpts);
  const [completeDeal, { loading: completeLoading }] = useMutation(COMPLETE_DEAL, {
    ...refetchOpts,
    onCompleted: () => refetch?.()
  });

  // Provider list for the compliance certificate company picker.
  // Page size is high — orgs rarely have 200+ active service providers.
  const { data: servicesData } = useQuery(GET_PROFESSIONAL_SERVICES, {
    variables: {
      pageIndex: 0,
      pageSize: 200,
      orderAsc: true,
      orderField: 'companyName',
      filters: { active: true }
    }
  });
  const providers = servicesData?.professionalServices?.items || [];

  const outstanding = deal.outstandingConditions || [];
  const allMet = outstanding.length === 0;
  const needsAttention = !!deal.needsAttention;
  const openRevertRequest = (deal.revertRequests || []).find((r) => r.status === 'PENDING_APPROVAL');

  const tp = deal.transferProgress || {};
  // Show on every deal — some clearance steps (SARS / Municipality / HOA) often
  // start being chased during the suspensive period, before the deal is concluded.
  const showTransferProgress = true;
  const isReadOnly = deal.status === 'COMPLETED' || deal.status === 'FAILED';
  const transferProgressHasData = !!tp.hoaApplicable || TRANSFER_MILESTONES.some((m) => tp[m.key]?.date);
  const transferProgressFilledCount = TRANSFER_MILESTONES.filter((m) => tp[m.key]?.date).length;
  const registeredDate = tp.registered?.date || null;
  const canMarkCompleted = deal.status === 'CONCLUDED' && !!registeredDate;

  const handleMilestoneDateChange = (key, value) => {
    setMilestone({
      variables: {
        dealId: deal.id,
        key,
        inputData: {
          date: value || null,
          notes: tp[key]?.notes || null
        }
      }
    });
  };

  const handleMilestoneNotesBlur = (key, value) => {
    if ((tp[key]?.notes || '') === value) return;
    setMilestone({
      variables: {
        dealId: deal.id,
        key,
        inputData: {
          date: tp[key]?.date ? toDateInput(tp[key].date) : null,
          notes: value || null
        }
      }
    });
  };

  const handleHoaToggle = (applicable) => {
    setHoaApplicable({ variables: { dealId: deal.id, applicable } });
  };

  const handleMarkCompleted = () => {
    if (!canMarkCompleted) return;
    if (!window.confirm('Mark this deal as Completed? This records the registration as the transfer date.')) return;
    completeDeal({
      variables: {
        id: deal.id,
        inputData: { completedDate: toDateInput(registeredDate) }
      }
    });
  };

  const cc = deal.complianceCertificates || {};
  const ccHasData = COMPLIANCE_CERTS.some(({ key }) => cc[key]?.companyId || cc[key]?.dateRequested || cc[key]?.dateReceived || cc[key]?.sentToAttorney);
  const ccFilledCount = COMPLIANCE_CERTS.filter(({ key }) => cc[key]?.companyId).length;

  const handleCertChange = (kind, patch) => {
    const current = cc[kind] || {};
    setComplianceCertificate({
      variables: {
        dealId: deal.id,
        kind,
        inputData: {
          companyId: patch.companyId !== undefined
            ? (patch.companyId || null)
            : (current.companyId?.id || current.companyId || null),
          dateRequested: patch.dateRequested !== undefined
            ? (patch.dateRequested || null)
            : (current.dateRequested ? toDateInput(current.dateRequested) : null),
          dateReceived: patch.dateReceived !== undefined
            ? (patch.dateReceived || null)
            : (current.dateReceived ? toDateInput(current.dateReceived) : null),
          sentToAttorney: patch.sentToAttorney !== undefined
            ? !!patch.sentToAttorney
            : !!current.sentToAttorney
        }
      }
    });
  };

  const handleConditionToggle = (cond, completed) => {
    if (cond.kind === 'DEPOSIT' || (cond.label === 'Deposit not yet received')) {
      // Omit receivedDate — the server defaults to new Date() when ticked
      // and clears it when un-ticked. Avoids sending a full ISO timestamp
      // through the date-only Date scalar.
      setDepositReceived({ variables: { dealId: deal.id, received: completed } });
      return;
    }
    if (cond.kind === 'BOND' || (cond.label === 'Bond not yet granted')) {
      setBondGranted({ variables: { dealId: deal.id, granted: completed } });
      return;
    }
    if (cond.kind === 'SMART_TRACK' || (cond.label === 'Send to Smart Track')) {
      setSentToSmartTrack({ variables: { dealId: deal.id, sent: completed } });
      return;
    }
    // Custom — find by matching description
    const match = (deal.suspensiveConditions || []).find((c) => c.description === cond.label);
    if (!match) return;
    updateCondition({
      variables: {
        dealId: deal.id,
        conditionId: match.id,
        inputData: { completed, dueDate: match.dueDate, description: match.description }
      }
    });
  };

  const handleRemoveCustom = (conditionId) => {
    if (!window.confirm('Remove this suspensive condition?')) return;
    removeCondition({ variables: { dealId: deal.id, conditionId } });
  };

  const handleAddCondition = (e) => {
    e.preventDefault();
    if (!newCondition.description.trim() || !newCondition.dueDate) return;
    addCondition({
      variables: {
        dealId: deal.id,
        inputData: {
          description: newCondition.description.trim(),
          dueDate: newCondition.dueDate
        }
      }
    });
  };

  const handleRevertSubmit = (e) => {
    e.preventDefault();
    if (!revertReason.trim()) return;
    if (isMaster) {
      revertDirectly({ variables: { dealId: deal.id, reason: revertReason.trim() } });
    } else {
      requestRevert({ variables: { dealId: deal.id, reason: revertReason.trim() } });
    }
  };

  const handleDecisionSubmit = (e) => {
    e.preventDefault();
    if (!decisionModal) return;
    const variables = {
      dealId: deal.id,
      requestId: decisionModal.requestId,
      decisionNote: decisionNote.trim() || null
    };
    if (decisionModal.kind === 'approve') approveRevert({ variables });
    else rejectRevert({ variables });
  };

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
      <div className="px-4 py-5 sm:px-6 border-b">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Status & Suspensive Conditions</h3>
            <p className="mt-1 text-sm text-gray-500">
              All suspensive conditions must be met before the deal can be marked as Concluded.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setFicaChecked({ variables: { dealId: deal.id, checked: !deal.ficaChecked } })}
              disabled={ficaLoading}
              title={deal.ficaChecked
                ? `FICA verified${deal.ficaCheckDate ? ` on ${formatDate(deal.ficaCheckDate)}` : ''} — click to revoke`
                : 'FICA not yet verified — click to mark verified'}
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                deal.ficaChecked
                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                  : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
              } disabled:opacity-50`}
            >
              FICA {deal.ficaChecked ? 'Verified' : 'Pending'}
            </button>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[deal.status] || 'bg-gray-100 text-gray-700'}`}>
              {deal.status}
            </span>
            {needsAttention && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Needs Attention
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Failure reason banner — shown for FAILED deals */}
      {deal.status === 'FAILED' && deal.failureReason && (
        <div className="px-4 py-3 bg-red-50 border-b border-red-200">
          <div className="text-sm font-medium text-red-700 mb-1">Failure Reason</div>
          <div className="text-sm text-red-900">{deal.failureReason}</div>
        </div>
      )}

      {/* Outstanding conditions list */}
      {deal.status === 'PENDING' && (
        <div className="border-b">
          {(() => {
            // Inject the "+ Add Custom Condition" row before the first
            // SMART_TRACK row, or at the end if smart track is already done.
            const smartTrackIdx = outstanding.findIndex((c) => c.kind === 'SMART_TRACK');
            const addRowAt = smartTrackIdx === -1 ? outstanding.length : smartTrackIdx;
            const addRow = (
              <li key="add-custom" className="px-4 py-2 bg-gray-50">
                <button
                  type="button"
                  onClick={() => setAddModalOpen(true)}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  + Add Custom Condition
                </button>
              </li>
            );
            const conditionRows = outstanding.map((c, i) => {
              const isOverdue = c.overdue;
              const customMatch = c.kind === 'CUSTOM'
                ? (deal.suspensiveConditions || []).find((sc) => sc.description === c.label)
                : null;
              return (
                <li key={`${c.kind}-${i}`} className={`px-4 py-3 flex items-center justify-between ${isOverdue ? 'bg-red-50' : ''}`}>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={false}
                      onChange={() => handleConditionToggle(c, true)}
                      disabled={depLoading || bondLoading || stLoading || condLoading}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{c.label}</div>
                      <div className="text-xs text-gray-500">
                        {c.kind} {c.dueDate ? `· due ${formatDate(c.dueDate)}` : ''} {isOverdue ? '· overdue' : ''}
                      </div>
                    </div>
                  </div>
                  {customMatch && (
                    <button
                      type="button"
                      onClick={() => handleRemoveCustom(customMatch.id)}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  )}
                </li>
              );
            });
            const rows = [...conditionRows.slice(0, addRowAt), addRow, ...conditionRows.slice(addRowAt)];

            if (outstanding.length === 0) {
              return (
                <>
                  <div className="px-4 py-4 text-sm text-green-700 bg-green-50">
                    ✓ All suspensive conditions are met. Ready to conclude.
                  </div>
                  <ul className="divide-y divide-gray-100">{addRow}</ul>
                </>
              );
            }
            return <ul className="divide-y divide-gray-100">{rows}</ul>;
          })()}

          {/* Already-met conditions for context */}
          {(deal.depositReceived || deal.bondGranted || deal.sentToSmartTrack || (deal.suspensiveConditions || []).some((c) => c.completed)) && (
            <div className="px-4 py-3 bg-green-50 border-t border-green-100 text-xs text-green-800">
              <div className="font-medium mb-1">Met:</div>
              <div className="space-y-1">
                {deal.depositReceived && (
                  <div>
                    ✓ Deposit received {deal.depositReceivedDate ? `on ${formatDate(deal.depositReceivedDate)}` : ''}
                    <button
                      onClick={() => setDepositReceived({ variables: { dealId: deal.id, received: false } })}
                      className="ml-2 text-xs underline text-gray-600 hover:text-gray-800"
                    >
                      undo
                    </button>
                  </div>
                )}
                {deal.bondGranted && (
                  <div>
                    ✓ Bond granted {deal.bondGrantedDate ? `on ${formatDate(deal.bondGrantedDate)}` : ''}
                    <button
                      onClick={() => setBondGranted({ variables: { dealId: deal.id, granted: false } })}
                      className="ml-2 text-xs underline text-gray-600 hover:text-gray-800"
                    >
                      undo
                    </button>
                  </div>
                )}
                {deal.sentToSmartTrack && (
                  <div>
                    ✓ Sent to Smart Track {deal.sentToSmartTrackDate ? `on ${formatDate(deal.sentToSmartTrackDate)}` : ''}
                    <button
                      onClick={() => setSentToSmartTrack({ variables: { dealId: deal.id, sent: false } })}
                      className="ml-2 text-xs underline text-gray-600 hover:text-gray-800"
                    >
                      undo
                    </button>
                  </div>
                )}
                {(deal.suspensiveConditions || []).filter((c) => c.completed).map((c) => (
                  <div key={c.id}>
                    ✓ {c.description} {c.completedDate ? `on ${formatDate(c.completedDate)}` : ''}
                    <button
                      onClick={() => updateCondition({
                        variables: {
                          dealId: deal.id,
                          conditionId: c.id,
                          inputData: { completed: false, dueDate: c.dueDate, description: c.description }
                        }
                      })}
                      className="ml-2 text-xs underline text-gray-600 hover:text-gray-800"
                    >
                      undo
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Transfer Progress (CONCLUDED → COMPLETED milestones) */}
      {showTransferProgress && (
        <details className="border-b group" open={transferProgressHasData}>
          <summary className="px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 list-none flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 text-gray-500 transition-transform group-open:rotate-90"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  Transfer Progress
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    {transferProgressFilledCount} / {TRANSFER_MILESTONES.length} milestones
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  Conveyancing milestones reported by the attorney. The day "Registered at Deeds Office" is filled is the transfer date.
                </div>
              </div>
            </div>
            <label
              className="inline-flex items-center gap-2 text-sm text-gray-700"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={!!tp.hoaApplicable}
                onChange={(e) => handleHoaToggle(e.target.checked)}
                disabled={isReadOnly || hoaLoading}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              HOA applies to this property
            </label>
          </summary>
          <ul className="divide-y divide-gray-100">
            {TRANSFER_MILESTONES.map((m) => {
              const milestone = tp[m.key] || {};
              const dateValue = toDateInput(milestone.date);
              const isDone = !!milestone.date;
              const conditionalDisabled = m.conditional === 'hoaApplicable' && !tp.hoaApplicable;
              const disabled = isReadOnly || milestoneLoading || conditionalDisabled;
              return (
                <li
                  key={m.key}
                  className={`px-4 py-3 ${conditionalDisabled ? 'bg-gray-50 opacity-60' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`mt-1 inline-flex items-center justify-center h-5 w-5 rounded-full text-xs font-medium ${
                        isDone ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-400'
                      }`}
                      aria-hidden
                    >
                      {isDone ? '✓' : '·'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="text-sm font-medium text-gray-900">
                          {m.label}
                          {m.conditional === 'hoaApplicable' && !tp.hoaApplicable && (
                            <span className="ml-2 text-xs text-gray-500 italic">(not applicable)</span>
                          )}
                        </div>
                        <input
                          type="date"
                          value={dateValue}
                          onChange={(e) => handleMilestoneDateChange(m.key, e.target.value)}
                          disabled={disabled}
                          className="border border-gray-300 rounded-md px-2 py-1 text-sm disabled:bg-gray-100"
                        />
                      </div>
                      <textarea
                        rows={1}
                        defaultValue={milestone.notes || ''}
                        onBlur={(e) => handleMilestoneNotesBlur(m.key, e.target.value)}
                        disabled={disabled}
                        placeholder="Notes (optional, e.g. attorney email reference)…"
                        className="mt-1 block w-full px-2 py-1 border border-gray-200 rounded text-xs text-gray-700 disabled:bg-gray-100"
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </details>
      )}

      {/* Compliance Certificates (electrical / gas / beetle) */}
      <details className="border-b group" open={ccHasData}>
        <summary className="px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 list-none flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <svg
              className="h-4 w-4 text-gray-500 transition-transform group-open:rotate-90"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <div>
              <div className="text-sm font-semibold text-gray-900">
                Compliance Certificates
                <span className="ml-2 text-xs font-normal text-gray-500">
                  {ccFilledCount} / {COMPLIANCE_CERTS.length} assigned
                </span>
              </div>
              <div className="text-xs text-gray-500">
                Electrical / Gas / Beetle clearance certificates and the contractors handling them.
              </div>
            </div>
          </div>
        </summary>
        <ul className="divide-y divide-gray-100">
          {COMPLIANCE_CERTS.map(({ key, label }) => {
            const cert = cc[key] || {};
            const companyId = cert.companyId?.id || cert.companyId || '';
            const requested = cert.dateRequested ? toDateInput(cert.dateRequested) : '';
            const received = cert.dateReceived ? toDateInput(cert.dateReceived) : '';
            const sent = !!cert.sentToAttorney;
            return (
              <li key={key} className="px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-gray-900">{label} Certificate</div>
                  <label className="inline-flex items-center gap-2 text-xs text-gray-700">
                    <input
                      type="checkbox"
                      checked={sent}
                      onChange={(e) => handleCertChange(key, { sentToAttorney: e.target.checked })}
                      disabled={isReadOnly || ccLoading}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    Sent to Attorney
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Company</label>
                    <select
                      value={companyId}
                      onChange={(e) => handleCertChange(key, { companyId: e.target.value })}
                      disabled={isReadOnly || ccLoading}
                      className="block w-full px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100"
                    >
                      <option value="">— Select —</option>
                      {providers.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.companyName || p.tradingName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Date Requested</label>
                    <input
                      type="date"
                      value={requested}
                      onChange={(e) => handleCertChange(key, { dateRequested: e.target.value })}
                      disabled={isReadOnly || ccLoading}
                      className="block w-full px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Date Received</label>
                    <input
                      type="date"
                      value={received}
                      onChange={(e) => handleCertChange(key, { dateReceived: e.target.value })}
                      disabled={isReadOnly || ccLoading}
                      className="block w-full px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100"
                    />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </details>

      {/* Action bar */}
      <div className="px-4 py-3 bg-gray-50 flex items-center justify-end flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {deal.status === 'PENDING' && (
            <button
              type="button"
              onClick={() => allMet && markConcluded({ variables: { id: deal.id } })}
              disabled={!allMet || concludeLoading}
              title={!allMet ? `Outstanding: ${outstanding.map((o) => o.label).join('; ')}` : ''}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {concludeLoading ? 'Concluding…' : 'Mark as Concluded'}
            </button>
          )}
          {deal.status === 'CONCLUDED' && !openRevertRequest && (
            <>
              <button
                type="button"
                onClick={() => setRevertModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-amber-300 rounded-md text-sm font-medium text-amber-700 bg-white hover:bg-amber-50"
              >
                {isMaster ? 'Revert to Pending' : 'Request Revert to Pending'}
              </button>
              <button
                type="button"
                onClick={handleMarkCompleted}
                disabled={!canMarkCompleted || completeLoading}
                title={!canMarkCompleted ? 'Fill in the "Registered at Deeds Office" date first' : ''}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {completeLoading ? 'Completing…' : 'Mark as Completed'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Pending revert request banner */}
      {openRevertRequest && (
        <div className="px-4 py-3 bg-amber-50 border-t border-amber-200">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-sm font-medium text-amber-900">
                Revert request awaiting master approval
              </div>
              <div className="text-xs text-amber-800 mt-1">
                Requested by {openRevertRequest.requestedBy ? `${openRevertRequest.requestedBy.firstName} ${openRevertRequest.requestedBy.lastName}` : 'unknown'}
                {' '}on {formatDate(openRevertRequest.requestedAt)}
              </div>
              <div className="text-sm text-gray-800 mt-2 italic">"{openRevertRequest.reason}"</div>
            </div>
            {isMaster && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setDecisionModal({ requestId: openRevertRequest.id, kind: 'approve' })}
                  className="px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => setDecisionModal({ requestId: openRevertRequest.id, kind: 'reject' })}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Revert history */}
      {(deal.revertRequests || []).length > 0 && (
        <details className="border-t">
          <summary className="px-4 py-3 text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50">
            Revert history ({deal.revertRequests.length})
          </summary>
          <div className="divide-y divide-gray-100 bg-gray-50">
            {[...deal.revertRequests].reverse().map((r) => (
              <div key={r.id} className="px-4 py-3 text-sm">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-gray-900">
                    {r.requestedBy ? `${r.requestedBy.firstName} ${r.requestedBy.lastName}` : 'Unknown user'}
                    <span className="text-xs text-gray-500 ml-2">{formatDate(r.requestedAt)}</span>
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${REVERT_STATUS_BADGE[r.status]}`}>
                    {REVERT_STATUS_LABEL[r.status]}
                  </span>
                </div>
                <div className="text-gray-700 mt-1 italic">"{r.reason}"</div>
                {r.decidedBy && (
                  <div className="text-xs text-gray-500 mt-1">
                    {r.status === 'DIRECT' ? 'By' : (r.status === 'APPROVED' ? 'Approved by' : 'Rejected by')}
                    {' '}{r.decidedBy.firstName} {r.decidedBy.lastName} · {formatDate(r.decidedAt)}
                    {r.decisionNote && <span> · "{r.decisionNote}"</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Add Custom Condition Modal */}
      <Modal open={addModalOpen} onClose={() => setAddModalOpen(false)} title="Add Suspensive Condition" size="md">
        <form onSubmit={handleAddCondition} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description*</label>
            <input
              type="text"
              value={newCondition.description}
              onChange={(e) => setNewCondition((p) => ({ ...p, description: e.target.value }))}
              placeholder="e.g. Purchaser must sell own property first"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Due Date*</label>
            <input
              type="date"
              value={newCondition.dueDate}
              onChange={(e) => setNewCondition((p) => ({ ...p, dueDate: e.target.value }))}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              onClick={() => setAddModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addLoading || !newCondition.description.trim() || !newCondition.dueDate}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {addLoading ? 'Adding…' : 'Add Condition'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Revert Request Modal */}
      <Modal
        open={revertModalOpen}
        onClose={() => setRevertModalOpen(false)}
        title={isMaster ? 'Revert Deal to Pending' : 'Request Revert to Pending'}
        size="md"
      >
        <form onSubmit={handleRevertSubmit} className="space-y-4">
          {!isMaster && (
            <p className="text-sm text-gray-600">
              Your request will be sent to a master user for approval. The deal stays in CONCLUDED until they decide.
            </p>
          )}
          {isMaster && (
            <p className="text-sm text-gray-600">
              The deal will revert to PENDING immediately. The reason is recorded in the audit log.
            </p>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason*</label>
            <textarea
              rows={4}
              value={revertReason}
              onChange={(e) => setRevertReason(e.target.value)}
              placeholder="Explain why this deal needs to revert to PENDING…"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              onClick={() => setRevertModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!revertReason.trim() || reqLoading || directLoading}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {(reqLoading || directLoading) ? 'Working…' : (isMaster ? 'Revert Now' : 'Submit Request')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Approve / Reject Decision Modal */}
      <Modal
        open={!!decisionModal}
        onClose={() => setDecisionModal(null)}
        title={decisionModal?.kind === 'approve' ? 'Approve Revert Request' : 'Reject Revert Request'}
        size="md"
      >
        <form onSubmit={handleDecisionSubmit} className="space-y-4">
          <p className="text-sm text-gray-600">
            {decisionModal?.kind === 'approve'
              ? 'Approving will revert this deal to PENDING immediately. The decision is recorded in the audit log.'
              : 'Rejecting keeps the deal in CONCLUDED. The decision is recorded in the audit log.'}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Decision note (optional)</label>
            <textarea
              rows={3}
              value={decisionNote}
              onChange={(e) => setDecisionNote(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              onClick={() => setDecisionModal(null)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={approveLoading || rejectLoading}
              className={`px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white disabled:opacity-50 ${decisionModal?.kind === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-700'}`}
            >
              {(approveLoading || rejectLoading) ? 'Working…' : (decisionModal?.kind === 'approve' ? 'Approve & Revert' : 'Reject Request')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default DealWorkflowPanel;
